import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  EditTwoTone,
  EyeTwoTone,
  FileDoneOutlined,
  ReconciliationOutlined,
  RightCircleTwoTone,
  StopTwoTone,
  ToolOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import _ from 'lodash';
import { getIntl } from 'umi';

import { fieldConfigurationMapping } from '@/utils/core/business-logic';
import { CoreEnums } from '@/utils/domain/constants';
import { sortFn } from '@/utils/infrastructure';

const toSorted = (arr = []) => {
  return arr.sort(sortFn);
};

const {
  CustomListCommandType,
  CustomListFieldType,
  EntityQueryOperationType,
  FormFieldType,
  FormFieldStringType,
} = CoreEnums;

const fileTypes = [
  FormFieldStringType.Attachment,
  FormFieldStringType.Image,
  FormFieldStringType.Photo,

  FormFieldType.Attachment,
  FormFieldType.Image,
  FormFieldType.Photo,
];

export const defaultHiddenCommands = {
  [CustomListCommandType.MassRequestsReject]: true,
  [CustomListCommandType.MassRequestsApprove]: true,
  [CustomListCommandType.CancelRequest]: true,
};

export const commandButtonType = {
  [CustomListCommandType.CancelRequest]: 'danger',
  [CustomListCommandType.MassRequestsReject]: 'danger',
  [CustomListCommandType.MassRequestsApprove]: 'success',
};

export const localCustomListCommands = [
  {
    id: 'rc-btn-reject',
    command: CustomListCommandType.RejectRequest,
    commandText: 'Rechazar Solicitud',
    route: 'processEngine/reject/{processInstanceId}/{actionInstanceId}',
    httpMethod: 'POST',
  },
  {
    id: 'rc-btn-massReject',
    command: CustomListCommandType.MassRequestsReject,
    commandText: 'Rechazar Solicitud',
    route: '',
    httpMethod: 'POST',
  },
];

export const localMapListCommands = [
  {
    id: 'rc-btn-approve',
    command: CustomListCommandType.ApproveRequest,
    commandText: 'Aprobar Solicitud',
    route: 'processEngine/approve/{processInstanceId}/{actionInstanceId}',
    httpMethod: 'POST',
  },
  {
    id: 'rc-btn-cancel',
    command: CustomListCommandType.CancelRequest,
    commandText: 'Cancelar Solicitud',
    route: 'processEngine/cancel/{processInstanceId}',
    httpMethod: 'POST',
  },
];

export const thCmdKeys = [
  CustomListCommandType.MassRequestsApprove,
  CustomListCommandType.MassRequestsReject,
  CustomListCommandType.Export,
  CustomListCommandType.CreateRequest,
  CustomListCommandType.CancelRequest,
];

export const trCmdKeys = [
  CustomListCommandType.ViewDetails,
  CustomListCommandType.CancelRequest,
  CustomListCommandType.CompleteRequestInformation,
  CustomListCommandType.ApproveRequest,
  CustomListCommandType.RejectRequest,
  CustomListCommandType.ExecuteTask,
  CustomListCommandType.SignDocument,
  CustomListCommandType.ReassignTask,
];

export const commandByKey = _.toPairs(CustomListCommandType).reduce(
  (lhs, [key, value]) => ({ ...lhs, [value]: key }),
  {},
);

export const renderCommandIcon = (command) => {
  return {
    [CustomListCommandType.ViewDetails]: <EyeTwoTone />,
    [CustomListCommandType.CancelRequest]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.CompleteRequestInformation]: (
      <CheckCircleTwoTone twoToneColor="#0dc26b" />
    ),
    [CustomListCommandType.ApproveRequest]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CustomListCommandType.RejectRequest]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CustomListCommandType.ExecuteTask]: <ToolOutlined />,
    [CustomListCommandType.SignDocument]: <FileDoneOutlined />,
    [CustomListCommandType.ReassignTask]: <ReconciliationOutlined />,
  }[command];
};

export const renderRequestCmdIcon = (cmd) => {
  return {
    [CoreEnums.RequestDetailCommandType.Approve]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CoreEnums.RequestDetailCommandType.Reject]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
    [CoreEnums.RequestDetailCommandType.Complete]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
    [CoreEnums.RequestDetailCommandType.Cancel]: <StopTwoTone twoToneColor="#ff5b70" />,
    [CoreEnums.RequestDetailCommandType.Details]: <EyeTwoTone />,
    [CoreEnums.RequestDetailCommandType.Continue]: <EditTwoTone />,
    [CoreEnums.RequestDetailCommandType.ReassignTask]: (
      <UserSwitchOutlined style={{ color: 'var(--ant-primary-color)' }} />
    ),
    [CoreEnums.RequestDetailCommandType.GoToActionFromCustomCommand]: {
      [CoreEnums.ActionCustomCommandType.Approve]: <CheckCircleTwoTone twoToneColor="#0dc26b" />,
      [CoreEnums.ActionCustomCommandType.Reject]: <CloseCircleTwoTone twoToneColor="#ff5b70" />,
      [CoreEnums.ActionCustomCommandType.Skip]: <RightCircleTwoTone twoToneColor="#faad14" />,
    }[cmd.customCommandType],
  }[cmd.command];
};

export const renderCommandLabel = (cmd) => {
  const id = {
    [CustomListCommandType.ViewDetails]: 'customList.text.detail',
    [CustomListCommandType.CancelRequest]: 'customList.text.cancel',
    [CustomListCommandType.CompleteRequestInformation]: 'customList.text.complete',
    [CustomListCommandType.ApproveRequest]: 'customList.text.approve',
    [CustomListCommandType.MassRequestsApprove]: 'customList.text.approve',
    [CustomListCommandType.MassRequestsReject]: 'customList.text.reject',
    [CustomListCommandType.RejectRequest]: 'customList.text.reject',
    [CustomListCommandType.ExecuteTask]: 'customList.text.execute',
    [CustomListCommandType.SignDocument]: 'customList.text.sign',
    [CustomListCommandType.ReassignTask]: 'customList.text.reassign',
  }[cmd.command];

  return id ? getIntl().formatMessage({ id }) : cmd.commandText;
};

export const commandResponseMessage = (command) => {
  return {
    [CustomListCommandType.CancelRequest]: 'custom.lists.text.message.cancel',
    [CustomListCommandType.ApproveRequest]: 'custom.lists.text.message.approve',
    [CustomListCommandType.MassRequestsApprove]: 'custom.lists.text.message.approve',
    [CustomListCommandType.ExecuteTask]: 'custom.lists.text.message.execute',
    [CustomListCommandType.SignDocument]: 'custom.lists.text.message.sign',
    [CustomListCommandType.ReassignTask]: 'custom.lists.text.message.reassign',
    [CoreEnums.RequestDetailCommandType.ReassignTask]: 'custom.lists.text.message.reassign',
    [CustomListCommandType.RejectRequest]: 'custom.lists.text.message.reject',
    [CustomListCommandType.MassRequestsReject]: 'custom.lists.text.message.reject',
  }[command];
};

export function fieldAndColumnMapper(fields = [], listId = null) {
  const formFields = [];
  const filtersConfig = [];

  const columns = toSorted(fields).map((item) => {
    const chunk = _.chunk(item.displayText, 3);

    const base = {
      id: item.id,
      name: item.dynamicEntityField?.internalName || item.internalName,
      label: item.displayText,
    };

    if (item?.type === CustomListFieldType.EntityField) {
      formFields.push({
        ...base,
        ...fieldConfigurationMapping(item.dynamicEntityField || item, fields),
      });
    }

    if (item.isFilterEnabled) {
      const knownCustomListFieldType = {
        [CustomListFieldType.InstanceCreated]: FormFieldType.Date,
        [CustomListFieldType.ApprovedDate]: FormFieldType.Date,
        [CustomListFieldType.RejectedDate]: FormFieldType.Date,
        [CustomListFieldType.EntityItemCreated]: FormFieldType.Date,
      };

      const isEntityDateField =
        item?.type === CustomListFieldType.EntityField &&
        item.dynamicEntityField?.type === FormFieldType.Date;
      const isCustomListField = item?.type === CustomListFieldType.Field || !isEntityDateField;

      const fieldConfig = {
        ...(item.dynamicEntityField || item || {}),
        id: item.id,
        operationType: EntityQueryOperationType.ListFilter,
        type: knownCustomListFieldType[item?.type] || FormFieldType.DataBaseRemoteList,
        ...(isEntityDateField && { type: FormFieldType.Date }),
        ...(isCustomListField && { type: FormFieldType.DataBaseRemoteList }),
      };

      const configMapping = fieldConfigurationMapping(fieldConfig, fields, false);
      filtersConfig.push({
        ...base,
        ...configMapping,
        ...(configMapping.nomenclature &&
          listId && { params: { ...configMapping.params, listId } }),
      });
    }

    const isEditable = item?.type === CustomListFieldType.EntityField && item.isEditEnabled;

    return {
      isEditable,
      key: item.internalName,
      title: item.displayText,
      dataIndex: item.internalName,
      type: item?.type,
      width: chunk.length > 3 ? chunk.length * 36 : 150,
      ...(isEditable && { dynamicEntityField: item.dynamicEntityField }),
      ...(fileTypes.includes(item?.type) && { width: 150 }),
      sorter: true,
    };
  });

  return { formFields, filtersConfig, columns };
}
